import { useQueryClient } from "@tanstack/react-query";import axiosInstance from "@services/index.services";import { TestImage } from "@assets/constant/index.constant";
import { Button } from "@components/ui/button";
import { GeneralCard, TableCard } from "@ui/internal/components/card";
import { useParams, useSearchParams } from "react-router";
import { CertificateForm, TemplateForm } from "../form/certificate.form";
import { QueryData } from "@utils/interface/index.interface";
import { CertificateData } from "@utils/interface/journey.interface";
import { useListData } from "@utils/query/index.query";
import { useMetaStore, useSelectionStore } from "@utils/states/index.state";
import { usePathname } from "@utils/hook/index.hook";
import { format } from "date-fns";
import { endpoint } from "@services/api.services";
import { useEffect, useState } from "react";
import { ColumnList, TemplateColumnList } from "../column/index.column";
import {
  exportToExcel,
  formatNumber,
  showToast,
} from "@utils/helper/index.helper";
import FilterPopover from "@ui/internal/components/filter-popover";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  BulkCertificateGenerate,
  BulkCertificateImport,
  DeleteGlimpsImage,
  SendCertificateEmail,
} from "@services/CertificateServices";
import { BoxDownloadIcon } from "@assets/icons";
import CSVUploader from "@ui/internal/components/upload-csv/UploadCsv";
import { FileUp } from "lucide-react";
import ErrorRowsModal from "@ui/internal/components/defective-row-extractor/DRX";

type ParamsType = {
  type: "manage-template" | "certificates";
};
const index = () => {
  const queryClient = useQueryClient();
  const { type } = useParams<ParamsType>();
  const [file, setFile] = useState<File | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [backendErrors, setBackendErrors] = useState<string[]>([""]);
  const [sendingEmailIds, setSendingEmailIds] = useState<Set<number | string>>(
    new Set()
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { search, date } = useMetaStore();
  const pathname = usePathname();
  const endpointData =
    type === "certificates"
      ? endpoint["certificate"]
      : type === "manage-template"
      ? endpoint["templates"]
      : "";
  /* ================================================= api calls logic ================================================= */
  const { data, isPending, refetch, error } = useListData<
    QueryData<CertificateData>
  >({
    params: {
      page: searchParams.get("tpage") || 1,
      size: searchParams.get("per") || 10,
      search,
      created_date__gte: date.from ? format(date.from, "yyyy-MM-dd") : "",
      created_date__lte: date.to ? format(date.to, "yyyy-MM-dd") : "",
      ordering: searchParams.get("ordering") || "order",
      course_batch__name:
        searchParams
          .get("data-category")
          ?.toLocaleLowerCase()
          ?.split("-")[0] === "all"
          ? ""
          : searchParams.get("data-category")?.toLocaleLowerCase(),
    },
    endpoint: endpointData,
    key: `${pathname}-listing`,
    window: true,
  });

  const { mutate: bulkCertificateImport } = useMutation({
    mutationFn: async (formdata: FormData) => BulkCertificateImport(formdata),
    onSuccess: (backendErrors: any) => {
      // Invalidate queries or perform other actions on success
      searchParams.delete("file");
      setSearchParams(searchParams);
       {
        backendErrors.data.errors.length > 0 &&
          setBackendErrors(backendErrors.data.errors);
      }

      showToast({
        title: "Certificate import",
        description: "Certificate Import done successfully!",
        type: "success",
      });
    },
    onError: (error: any) => {
      searchParams.delete("file");
      setSearchParams(searchParams);

      showToast({
        title: "Certificate import",
        description: error.message || "Certificate went wrong try again!",
        type: "error",
      });
    },
  });

  const { mutate: bulkCertificateGenerate } = useMutation({
    mutationFn: async (formdata: FormData) => BulkCertificateGenerate(formdata),
    onSuccess: (backendErrors: any) => {
      // Invalidate queries or perform other actions on success
      searchParams.delete("generate");
      setSearchParams(searchParams);
      {backendErrors.data.errors.length > 0 && setShowErrorModal(true)}
      {
        backendErrors.data.errors.length > 0 &&
          setBackendErrors(backendErrors.data.errors);
      }

      showToast({
        title: "Generate Certificate",
        description: "Generate Certificate done successfully!",
        type: "success",
      });
    },
    onError: (error: any) => {
      searchParams.delete("generate");
      setSearchParams(searchParams);

      showToast({
        title: "Generate Certificate",
        description: error.message || "Something went wrong try again!",
        type: "error",
      });
    },
  });

  const { mutate: deleteGlimpsImage, isPending: DeletePending } = useMutation({
    mutationFn: (id: number) => DeleteGlimpsImage(id),
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      console.log("Error deleting glimps image:", error);
    },
  });

  const { mutate: sendCertificateEmail } = useMutation({
    mutationFn: (row: any) => {
      return axiosInstance.post(`/certificate/${row.id}/send-only/`, {}, {
        headers: { "Content-Type": "application/json" },
      });
    },
    onMutate: (row) => {
      setSendingEmailIds((ids) => new Set(ids).add(row.id));
    },
    onSuccess: (_data, variables) => {
      // Optimistically update the query cache so the UI updates instantly
      queryClient.setQueryData([`${pathname}-listing`], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            results: oldData.data.results.map((cert: any) =>
              cert.id === variables.id
                ? { ...cert, email_sent_at: new Date().toISOString() }
                : cert
            ),
          },
        };
      });
      refetch();
      showToast({
        title: "Email Sent",
        description: "Certificate email sent successfully!",
        type: "success",
      });
    },
    onError: (error: any) => {
      showToast({
        title: "Email Failed",
        description: error?.response?.data?.detail || error?.message || "Failed to send certificate email.",
        type: "error",
      });
    },
    onSettled: (_data, _error, row) => {
      setSendingEmailIds((ids) => {
        const nextIds = new Set(ids);
        nextIds.delete(row.id);
        return nextIds;
      });
    },
  });

  const handleSendEmail = (row: any) => {
    if (row?.id) {
      sendCertificateEmail(row);
    }
  };

  // rollback
  useEffect(() => {
    if (error?.status === 404 && error?.data?.detail === "Invalid page.") {
      searchParams.set("tpage", "1");
      setSearchParams(searchParams);
    }
  }, [error]);

  const columns =
    type === "certificates"
      ? ColumnList({
          refetch: refetch,
          deleteGlimpsImage: deleteGlimpsImage,
          DeletePending: DeletePending,
          onSendEmail: handleSendEmail,
          sendingEmailIds,
          sentEmailIds: new Set(),
        })
      : type === "manage-template"
      ? TemplateColumnList({ refetch: refetch })
      : [];
  const { selectedRowDetails } = useSelectionStore();

  return (
    <>
      <TableCard
        bulkActions
        orientationPosition={type == "manage-template" ? "up" : false}
        inject_ui_table_actions={
          <>
            <FilterPopover
              filters={[
                {
                  label: "Course And Batch",
                  endpointValue: "course-batch",
                  format: {
                    key: "name",
                    value: "id",
                  },
                },
              ]}
              title="Filter"
              triggerText="Filter"
            />

            {data && data?.data?.results?.length > 0 && (
              <Button
                type="button"
                onClick={() =>
                  exportToExcel(
                    selectedRowDetails?.length > 0
                      ? selectedRowDetails
                      : data?.data?.results ?? [],
                    `${type}.xlsx`
                  )
                }
                variant={"outline"}
              >
                Export .xlsx
              </Button>
            )}

            <Button
              startIcon={<BoxDownloadIcon />}
              type="button"
              className="gap-0"
              onClick={() => {
                searchParams.set("file", "true");
                setSearchParams(searchParams);
              }}
              // onClick={() => {
              //   document.getElementById("import-xlxs-file")?.click();
              // }}
              variant={"outline"}
            >
              Import .xlsx
            </Button>

            <Button
              startIcon={<FileUp />}
              type="button"
              className="gap-0"
              onClick={() => {
                searchParams.set("generate", "true");
                setSearchParams(searchParams);
              }}
              // onClick={() => {
              //   document.getElementById("import-xlxs-file")?.click();
              // }}
              variant={"outline"}
            >
              Generate Certificates
            </Button>
          </>
        }
        table_content={{
          mode: "default",
          type: type || "",
        }}
        formId={type}
        tableHeaderData={
          pathname == "certificate-manage-template"
            ? []
            : [
                {
                  id: 1,
                  name: "All",
                  count: formatNumber((data?.data as any)?.total_count as number) || 0,
                },
                ...Object.keys((data?.data as any)?.course_batch_counts || {}).map(
                  (key, index) => ({
                    id: index + 1,
                    name: key,
                    count: formatNumber(
                      (data?.data as any)?.course_batch_counts?.[key] || 0
                    ),
                  })
                ),
              ]
        }
        grid={
          <GeneralCard
            isChecked={false}
            type={
              type === "certificates"
                ? "mentor"
                : type === "manage-template"
                ? "template"
                : ""
            }
            content={{
              images: [TestImage, TestImage, TestImage],
              title: "Aayush Maharjan",
              subtitle: "Frontend Developer",
            }}
            onDelete={() => {}}
            onEdit={() => {}}
          />
        }
        form={
          type == "manage-template" ? <TemplateForm /> : <CertificateForm />
        }
        endpoint={endpointData}
        isPending={isPending}
        columns={columns}
        refetch={refetch}
        rows={data?.data?.results ?? []}
        count={data?.data?.count ?? 10}
      />
      {searchParams.get("file") && (
        <Dialog
          open={searchParams.get("file") ? true : false}
          onOpenChange={() => {
            searchParams.delete("file");
            setSearchParams(searchParams);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Upload File
                <a
                  className="text-green-800 underline ml-4"
                  href="https://docs.google.com/spreadsheets/d/1N1R0yvfgCEeELWw8QcwMxCnvEUfBcMYL3SEucnUfrnQ/edit?gid=0#gid=0"
                  target="_blank"
                >
                  Use this Format
                </a>
              </DialogTitle>

              <DialogDescription>
                <CSVUploader
                  onUpload={bulkCertificateImport}
                  setFile={setFile}
                  file={file}
                />
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
      {searchParams.get("generate") && (
        <Dialog
          open={searchParams.get("generate") ? true : false}
          onOpenChange={() => {
            searchParams.delete("generate");
            setSearchParams(searchParams);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Upload File
                <a
                  className="text-green-800 underline ml-4"
                  href="https://docs.google.com/spreadsheets/d/1WjAOv-GbkS-waaZ2AiFyCMJWLH5A24xanJXX8HVg-pM/edit?gid=0#gid=0"
                  target="_blank"
                >
                  Use this Format
                </a>
              </DialogTitle>

              <DialogDescription>
                <CSVUploader
                  onUpload={bulkCertificateGenerate}
                  setFile={setFile}
                  file={file}
                />
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      )}
      <ErrorRowsModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        errors={backendErrors}
        originalFile={file}
      />
      ;
    </>
  );
};

export default index;
